import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { usePermissions } from '../context/PermissionsContext';

const Sidebar: React.FC = () => {
  const { state, actions } = useApp();
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'fa-tachometer-alt',
      path: '/',
      permission: 'dashboard.view' as const,
    },
    {
      id: 'management',
      label: 'Management',
      isHeading: true,
    },
    {
      id: 'channels',
      label: 'Channels',
      icon: 'fa-route',
      path: '/channels',
      permission: 'channels.view' as const,
    },
    {
      id: 'gateways',
      label: 'Gateways',
      icon: 'fa-server',
      path: '/gateways',
      permission: 'gateways.view' as const,
    },
    {
      id: 'mediators',
      label: 'Mediators',
      icon: 'fa-exchange-alt',
      path: '/mediators',
      permission: 'mediators.view' as const,
    },
    {
      id: 'integrations',
      label: 'Integrations',
      icon: 'fa-puzzle-piece',
      path: '/integrations',
    },
    {
      id: 'pipes',
      label: 'Pipes',
      icon: 'fa-plug',
      path: '/pipes',
      permission: 'pipes.view' as const,
    },
    {
      id: 'trust-registries',
      label: 'Registries',
      icon: 'fa-shield-alt',
      path: '/trust-registries',
      permission: 'trust_registries.view' as const,
    },
    {
      id: 'secrets',
      label: 'Secrets',
      icon: 'fa-key',
      path: '/secrets',
      permission: 'secrets.view' as const,
    },
    {
      id: 'identities',
      label: 'Identities',
      icon: 'fa-users',
      path: '/identities',
    },
    {
      id: 'users',
      label: 'Users',
      icon: 'fa-users',
      path: '/users',
      permission: 'users.view' as const,
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: 'fa-sliders-h',
      path: '/settings',
      permission: 'settings.view' as const,
    },
    {
      id: 'monitoring',
      label: 'Monitoring',
      isHeading: true,
    },
    {
      id: 'metrics',
      label: 'Metrics',
      icon: 'fa-chart-bar',
      path: '/metrics',
      permission: 'metrics.view' as const,
    },
    {
      id: 'logs',
      label: 'Logs',
      icon: 'fa-file-alt',
      path: '/logs',
      permission: 'logs.view' as const,
    },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: 'fa-tasks',
      path: '/tasks',
    },
  ];

  // Filter menu items based on permissions
  const visibleMenuItems = menuItems.filter(item => {
    if (item.isHeading) return true;
    if (!item.permission) return true; // No permission required
    return hasPermission(item.permission);
  });

  const getNavItemClass = (item: typeof menuItems[0]) => {
    if (item.isHeading) return '';
    
    // For path-based navigation
    if (item.path) {
      return `nav-item ${location.pathname === item.path ? 'active' : ''}`;
    }
    
    return 'nav-item';
  };

  return (
    <ul 
      className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion ${
        state.sidebarCollapsed ? 'toggled' : ''
      }`} 
      id="accordionSidebar"
    >
      {/* Sidebar - Brand */}
      <li className="sidebar-brand-wrapper">
        <button className="sidebar-brand d-flex flex-column align-items-center justify-content-center py-4 border-0 bg-transparent w-100" onClick={() => navigate('/')}>
          <div className="sidebar-brand-icon w-100 px-3">
            <img 
              src="/dashboard/images/aff_logo_reversed.svg" 
              alt="Affinidi Logo" 
              className="sidebar-logo" 
              id="sidebar-logo-full"
              style={{ display: state.sidebarCollapsed ? 'none' : 'block' }}
            />
            <img 
              src="/dashboard/images/aff_logo_sym_rev.svg" 
              alt="Affinidi Logo" 
              className="sidebar-logo" 
              id="sidebar-logo-compact"
              style={{ display: state.sidebarCollapsed ? 'block' : 'none' }}
            />
          </div>
        </button>
      </li>

      {/* Divider */}
      <li><hr className="sidebar-divider my-0" /></li>

      {/* Navigation Items */}
      {visibleMenuItems.map((item) => {
        if (item.isHeading) {
          return (
            <li key={item.id} className="sidebar-heading-wrapper">
              <hr className="sidebar-divider" />
              <div className="sidebar-heading">
                {item.label}
              </div>
            </li>
          );
        }

        // Handle path-based navigation
        return (
          <li key={item.id} className={getNavItemClass(item)}>
            <Link 
              className="nav-link" 
              to={item.path!}
            >
              <i className={`fas fa-fw ${item.icon}`}></i>
              <span>{item.label}</span>
            </Link>
          </li>
        );
      })}

      {/* Divider */}
      <li><hr className="sidebar-divider d-none d-md-block" /></li>

      {/* Sidebar Toggler */}
      <li className="text-center d-none d-md-inline">
        <button 
          className="rounded-circle border-0" 
          id="sidebarToggle" 
          title="Toggle Sidebar"
          onClick={actions.toggleSidebar}
        />
      </li>
    </ul>
  );
};

export default Sidebar;